import React, {useMemo} from "react";
import {useGetAllUsersQuery} from "../../../../../../store/services/api/user-api";
import {Divider, Form, Input, Select, Tag,Collapse } from "antd";
import FilterContainer, {CoreFiltersBaseProps} from "../../../../components/FilterContainer";
import DateRangePicker from "../../../../../../components/forms/controls/DateRangePicker";
import {WorkOrderPriority, WorkOrderStatus, WorkOrderType} from "../../../../../../store/data/entities/work-order";
import {
  useGetAllBuildingsQuery,
  useGetAllFloorsQuery,
  useGetAllLocationsQuery
} from "../../../../../../store/services/api/building-api";
import {DateTime} from "luxon";

type Building = {
  id: string;
  name: string;
};

type Floor = {
  id: string;
  name: string;
  buildingId: string;
};

type Location = {
  id: string;
  name: string;
  floorId: string;
};

export default function WorkOrdersListScreenFilters({filter, onSubmit}: CoreFiltersBaseProps) {
  const { Panel } = Collapse;
  const {data: allUsers} = useGetAllUsersQuery();
  const buildingLeads = useMemo(() => {
    return allUsers?.filter(item => item.role === 'BuildingLead') ?? [];
  }, [allUsers]);
  const technicians = useMemo(() => {
    return allUsers?.filter(item => item.role === 'Technician') ?? [];
  }, [allUsers]);
  const portalUsers = useMemo(() => {
    return allUsers?.filter(item => item.role !== 'Technician') ?? [];
  }, [allUsers]);

  const { data: buildings } = useGetAllBuildingsQuery(null);
  const { data: floors } = useGetAllFloorsQuery(null);
  const { data: locations } = useGetAllLocationsQuery(null);

  // Build maps for names
  const buildingMap: Map<string, string> = new Map(
    (buildings || []).map((b: Building) => [b.id, b.name])
  );
  const floorMap: Map<string, { name: string; buildingId: string }> = new Map(
    (floors || []).map((f: Floor) => [f.id, { name: f.name, buildingId: f.buildingId }])
  );
  // Get current filter values
  const selectedBuildingIds: string[] = filter.getFilter('buildingId') || [];
  const selectedFloorIds: string[] = filter.getFilter('floorId') || [];
  const selectedLocationIds: string[] = filter.getFilter('locationId') || [];

  /**
   * Handle Building changes
   */
  const handleOnBuildingChange = (buildingIds: string[]) => {
    filter.setFilter('buildingId', buildingIds);

    // prune floors
    const currentFloors: string[] = filter.getFilter('floorId') || [];
    const validFloors = (floors || [])
      .filter(f => buildingIds.includes(f.buildingId))
      .map(f => f.id);

    const prunedFloors = currentFloors.filter(floorId =>
      validFloors.includes(floorId)
    );
    filter.setFilter('floorId', prunedFloors);

    // prune locations 
    const currentLocations: string[] = filter.getFilter('locationId') || [];
    const validLocations = (locations || [])
      .filter(l => validFloors.includes(l.floorId))
      .map(l => l.id);

    const prunedLocations = currentLocations.filter(locId =>
      validLocations.includes(locId)
    );
    filter.setFilter('locationId', prunedLocations);
  };

  /**
   * Handle Floor changes
   */
  const handleOnFloorChange = (floorIds: string[]) => {
    filter.setFilter('floorId', floorIds);

    // prune locations
    const currentLocations: string[] = filter.getFilter('locationId') || [];
    const validLocations = (locations || [])
      .filter(l => floorIds.includes(l.floorId))
      .map(l => l.id);

    const prunedLocations = currentLocations.filter(locId =>
      validLocations.includes(locId)
    );
    filter.setFilter('locationId', prunedLocations);
  };

  /**
   * Floors filtered by buildings
   */
  const filteredFloors: Floor[] = !selectedBuildingIds.length
    ? (floors || [])
    : (floors || []).filter(f => selectedBuildingIds.includes(f.buildingId));

  const sortedFloors = filteredFloors
    .map(f => {
      const buildingName = buildingMap.get(f.buildingId) ?? '';
      return { ...f, label: `[${buildingName}] ${f.name}` };
    })
    .sort((a, b) => {
      const buildingA = buildingMap.get(a.buildingId) ?? '';
      const buildingB = buildingMap.get(b.buildingId) ?? '';
      return buildingA === buildingB
        ? a.name.localeCompare(b.name)
        : buildingA.localeCompare(buildingB);
    });

  /**
   * Locations filtered by floors
   */
  const filteredLocations: Location[] = !selectedFloorIds.length
    ? (locations || [])
    : (locations || []).filter(l => selectedFloorIds.includes(l.floorId));

  const sortedLocations = filteredLocations
    .map(l => {
      const floor = floorMap.get(l.floorId);
      const buildingName = floor ? buildingMap.get(floor.buildingId) ?? '' : '';
      const floorName = floor?.name ?? '';
      return {
        ...l,
        label: `[${buildingName}] [${floorName}] ${l.name}`,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  // Create a map for location labels
  const locationLabelMap: Map<string, string> = new Map(
    sortedLocations.map(l => [l.id, l.label])
  );

  const floorLabelMap: Map<string, string> = new Map(
      sortedFloors.map(l => [l.id, l.label])
    );

  
  // Custom tag render for locations with wrapped text
  const locationTagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const locationLabel = locationLabelMap.get(value) || label;
    
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{ 
          margin: '2px', 
          padding: '2px 6px',
          maxWidth: '100%',
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          height: 'auto'
        }}
      >
        {locationLabel}
      </Tag>
    );
  };

  const floorTagRender = (props: any) => {
        const { label, value, closable, onClose } = props;
        const floorLabel = floorLabelMap.get(value) || label;
        
        return (
          <Tag
            closable={closable}
            onClose={onClose}
            style={{ 
              margin: '2px', 
              padding: '2px 6px',
              maxWidth: '100%',
              whiteSpace: 'normal',
              lineHeight: '1.4em',
              height: 'auto'
            }}
          >
            {floorLabel}
          </Tag>
        );
      };


  return (
    <FilterContainer
      onResetFilters={filter.resetFilters}
      onSubmit={onSubmit}
      isSubmitting={undefined}
    >
      <Collapse defaultActiveKey={['general','assignment','dates','priorityStatus','location']} ghost>
      <Panel header="General Info" key="general">
        <Form layout='vertical'>
          <Form.Item label='Reference'>
            <Input
              allowClear
              value={filter.getFilter('reference')}
              onChange={e => filter.setFilter('reference', e.target.value)}
            />
          </Form.Item>
          <Form.Item label='Parent WO Reference'>
            <Input
              allowClear
              value={filter.getFilter('parentReference')}
              onChange={e => filter.setFilter('parentReference', e.target.value)}
            />
          </Form.Item>
          <Form.Item label='Description'>
            <Input
              allowClear
              value={filter.getFilter('description')}
              onChange={e => filter.setFilter('description', e.target.value)}
            />
          </Form.Item>

          <Form.Item label='Code'>
            <Input
              allowClear
              value={filter.getFilter('code')}
              onChange={e => filter.setFilter('code', e.target.value)}
            />
          </Form.Item>

        </Form>
      </Panel>

      
      <Panel header="Assignment" key="assignment">
        <Form layout='vertical'>
          <Form.Item label='Owner'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('buildingLeadId')}
            onChange={val => filter.setFilter('buildingLeadId', val)}
            options={buildingLeads?.map(item => ({
              label: item.name,
              value: item.id
            }))}
          />
        </Form.Item>

        <Form.Item label='Technician'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('technicianId')}
            onChange={val => filter.setFilter('technicianId', val)}
            options={technicians?.map(item => ({
              label: item.name,
              value: item.id
            }))}
          />
        </Form.Item>
        <Form.Item label='Created By'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('createdByUserId')}
            onChange={val => filter.setFilter('createdByUserId', val)}
            options={allUsers?.map(item => ({
              label: item.name,
              value: item.id
            }))}
          />
        </Form.Item>
        <Form.Item label='Closed By'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('closedByUserId')}
            onChange={val => filter.setFilter('closedByUserId', val)}
            options={allUsers?.map(item => ({
              label: item.name,
              value: item.id
            }))}
          />
        </Form.Item>

        </Form>
      </Panel>

      <Panel header="Dates" key="dates">
        <Form layout='vertical'>
          <Form.Item label='Scheduled Date'>
          <DateRangePicker
            rangeSelectionBehavior='future'
            onStartDateChange={(value) => {
              filter.setFilter('startDate', value?.toISO() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('endDate', value?.toISO() ?? undefined);
            }}
            startDateValue={filter.getFilter('startDate') ? DateTime.fromISO(filter.getFilter('startDate')) : undefined}
            endDateValue={filter.getFilter('endDate') ? DateTime.fromISO(filter.getFilter('endDate')) : undefined}
          />
        </Form.Item>
        <Form.Item label='Must Finish By'>
          <DateRangePicker
            rangeSelectionBehavior='future'
            onStartDateChange={(value) => {
              filter.setFilter('scheduledFinishStartDate', value?.toISO() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('scheduledFinishEndDate', value?.toISO() ?? undefined);
            }}
            startDateValue={filter.getFilter('scheduledFinishStartDate') ? DateTime.fromISO(filter.getFilter('scheduledFinishStartDate')) : undefined}
            endDateValue={filter.getFilter('scheduledFinishEndDate') ? DateTime.fromISO(filter.getFilter('scheduledFinishEndDate')) : undefined}
          />
        </Form.Item>
        <Form.Item label='Created On'>
          <DateRangePicker
            onStartDateChange={(value) => {
              filter.setFilter('createdOnStartDate', value?.toISO() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('createdOnEndDate', value?.toISO() ?? undefined);
            }}
            startDateValue={filter.getFilter('createdOnStartDate') ? DateTime.fromISO(filter.getFilter('createdOnStartDate')) : undefined}
            endDateValue={filter.getFilter('createdOnEndDate') ? DateTime.fromISO(filter.getFilter('createdOnEndDate')) : undefined}
          />
        </Form.Item>
        <Form.Item label='Closed On'>
          <DateRangePicker
            onStartDateChange={(value) => {
              filter.setFilter('closedOnStartDate', value?.toISO() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('closedOnEndDate', value?.toISO() ?? undefined);
            }}
            startDateValue={filter.getFilter('closedOnStartDate') ? DateTime.fromISO(filter.getFilter('closedOnStartDate')) : undefined}
            endDateValue={filter.getFilter('closedOnEndDate') ? DateTime.fromISO(filter.getFilter('closedOnEndDate')) : undefined}
          />
        </Form.Item>

        <Form.Item label='Labour Entries'>
          <DateRangePicker
          format="dd/MM/yyyy"
            onStartDateChange={(value) => {
              filter.setFilter('labourEntryStartDate', value?.toISODate() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('labourEntryEndDate', value?.toISODate() ?? undefined);
            }}
            startDateValue={filter.getFilter('labourEntryStartDate') ? DateTime.fromISO(filter.getFilter('labourEntryStartDate')) : undefined}
            endDateValue={filter.getFilter('labourEntryEndDate') ? DateTime.fromISO(filter.getFilter('labourEntryEndDate')) : undefined}
          />
        </Form.Item>

        </Form>
      </Panel>

      <Panel header="Status" key="priorityStatus">
        <Form layout='vertical'>
          <Form.Item label='Priority'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).children  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('priority')}
            onChange={val => filter.setFilter('priority', val)}
          >
            <Select.Option value={WorkOrderPriority.Standard}>Standard</Select.Option>
            <Select.Option value={WorkOrderPriority.Important}>Important</Select.Option>
            <Select.Option value={WorkOrderPriority.Urgent}>Urgent</Select.Option>
            <Select.Option value={WorkOrderPriority.Emergency}>Emergency</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label='Status'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).children  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('statuses')}
            onChange={val => filter.setFilter('statuses', val)}
          >
            <Select.Option value={WorkOrderStatus.PendingApproval}>Pending Approval</Select.Option>
            <Select.Option value={WorkOrderStatus.Approved}>Approved</Select.Option>
            <Select.Option value={WorkOrderStatus.Rejected}>Rejected</Select.Option>
            <Select.Option value={WorkOrderStatus.InProgress}>In Progress</Select.Option>
            <Select.Option value={WorkOrderStatus.Completed}>Completed</Select.Option>
            <Select.Option value={WorkOrderStatus.Closed}>Closed</Select.Option>
            <Select.Option value={WorkOrderStatus.Cancelled}>Cancelled</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item label='Type'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).children  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('type')}
            onChange={val => filter.setFilter('type', val)}
          >
            <Select.Option value={WorkOrderType.Preventative}>Preventative</Select.Option>
            <Select.Option value={WorkOrderType.Corrective}>Corrective</Select.Option>
            <Select.Option value={WorkOrderType.Improvement}>Improvement</Select.Option>
          </Select>
        </Form.Item>

        </Form>
      </Panel>

      <Panel header="Location" key="location">
        <Form layout='vertical'>
          <Form.Item label="Building">
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = String(option?.label ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            value={selectedBuildingIds}
            onChange={handleOnBuildingChange}
            options={buildings?.map(item => ({
              label: item.name,
              value: item.id,
            }))}
          />
        </Form.Item>

        <Form.Item label="Floor">
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = String(option?.searchText ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            value={selectedFloorIds}
            onChange={handleOnFloorChange}
            tagRender={floorTagRender}
            dropdownRender={menu => (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {menu}
              </div>
            )}
            optionLabelProp="label"
            options={sortedFloors.map(item => ({
              label: (
                <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                  {item.label}
                </div>
              ),
              value: item.id,
              searchText: item.label,
            }))}
          />
        </Form.Item>

        <Form.Item label="Location">
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = String(option?.searchText ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            value={selectedLocationIds}
            onChange={(val) => filter.setFilter('locationId', val)}
            tagRender={locationTagRender}
            dropdownRender={menu => (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {menu}
              </div>
            )}
            optionLabelProp="label"
            options={sortedLocations.map(item => ({
              label: (
                <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                  {item.label}
                </div>
              ),
              value: item.id,
              searchText: item.label,
            }))}
          />
        </Form.Item>

        </Form>
      </Panel>

      </Collapse>
    </FilterContainer>
  )
}
